/**
 * Карточка объекта: что именно выбрал пользователь и что об этом известно.
 *
 * Показываются ВСЕ атрибуты из тайла, а не заранее выбранный набор. Состав
 * полей в выгрузках генплана меняется от документа к документу, и «красивый»
 * фиксированный список гарантированно скроет именно то поле, ради которого
 * человек и кликнул.
 */

// Точечные импорты вместо @turf/turf: полный бандл turf тянет в сборку
// сотни килобайт геообработки, из которых здесь нужны две функции.
import { area } from "@turf/area";
import { length } from "@turf/length";
import type { Feature, Geometry } from "geojson";
import { describeZone, readZoneCode } from "~/config/zones";
import { clear, el } from "./dom";

export interface SelectedFeature {
  layerTitle: string;
  sourceLayer: string;
  properties: Record<string, unknown>;
  /**
   * Геометрия есть не у всякого выбранного объекта. Справка о земельном
   * участке приходит с чужого сервера одними атрибутами — контур он не
   * отдаёт. Обещать в карточке площадь и подсветку в таком случае нельзя.
   */
  geometry: Geometry | null;
  /** Пояснение к заголовку, когда оно не выводится из кода зоны. */
  description?: string | null;
  /** Откуда сведения, если не из наших тайлов. */
  sourceNote?: string | null;
}

export interface FeatureCardCallbacks {
  onClose: () => void;
  onCopyLink: () => void;
}

export function renderFeatureCard(
  container: HTMLElement,
  feature: SelectedFeature | null,
  callbacks: FeatureCardCallbacks,
): void {
  clear(container);

  if (!feature) {
    container.hidden = true;
    return;
  }
  container.hidden = false;

  const zoneCode = readZoneCode(feature.properties);
  const zone = describeZone(zoneCode);

  const heading = zone?.title ?? (zoneCode ? `Зона ${zoneCode}` : feature.layerTitle);

  const nodes: (Node | null)[] = [
    el(
      "header",
      { class: "card__header" },
      el(
        "div",
        { class: "card__heading" },
        zoneCode ? el("span", { class: "card__code", text: zoneCode }) : null,
        el("h2", { class: "card__title", text: heading }),
      ),
      el("button", {
        class: "card__close",
        text: "×",
        attrs: { type: "button", "aria-label": "Закрыть карточку объекта" },
        on: { click: callbacks.onClose },
      }),
    ),
    describeText(zone?.description ?? feature.description),
    measurements(feature, heading),
    // «Атрибутов нет» уместно сказать про объект слоя: у него они должны быть,
    // и их отсутствие — сигнал проверить данные. Про функциональную зону, взятую
    // с растрового листа, это просто шум: атрибутов у растра не бывает, и об
    // этом уже сказано в описании слоя.
    Object.keys(feature.properties).length === 0 && feature.description
      ? null
      : attributesTable(feature.properties),
    el(
      "footer",
      { class: "card__footer" },
      el("button", {
        class: "button button--ghost",
        text: "Скопировать ссылку на объект",
        attrs: { type: "button" },
        on: { click: callbacks.onCopyLink },
      }),
    ),
  ];

  container.append(...nodes.filter((node): node is Node => node !== null));
}

function describeText(text: string | null | undefined): HTMLElement | null {
  return text ? el("p", { class: "card__description", text }) : null;
}

/** Поля, в которых документ сам несёт площадь. Порядок — приоритет. */
const AREA_FIELDS: [pattern: RegExp, toSquareMeters: (value: number) => number][] = [
  [/^(ploshchad|ploschad|area)_?(kv_?m|m2|sq_?m)$/i, (v) => v],
  // «Площадь, га» и распространённое сокращение S_GA.
  [/^(ploshchad|ploschad|area)_?ga$/i, (v) => v * 10_000],
  [/^s_ga$/i, (v) => v * 10_000],
  [/^(ploshchad|ploschad|area|shape_?area)$/i, (v) => v],
];

function documentArea(properties: Record<string, unknown>): { label: string; value: number } | null {
  for (const [pattern, convert] of AREA_FIELDS) {
    for (const [key, raw] of Object.entries(properties)) {
      if (!pattern.test(key)) continue;
      const value = typeof raw === "number" ? raw : Number.parseFloat(String(raw).replace(",", "."));
      if (Number.isFinite(value) && value > 0) return { label: key, value: convert(value) };
    }
  }
  return null;
}

function measurements(feature: SelectedFeature, heading: string): HTMLElement | null {
  const rows: [string, string][] = [];
  let footnote: string | null = feature.sourceNote ?? null;

  const geometry = feature.geometry;
  const geojson: Feature | null = geometry
    ? { type: "Feature", geometry, properties: {} }
    : null;

  const isPolygon = geometry?.type === "Polygon" || geometry?.type === "MultiPolygon";
  const isLine = geometry?.type === "LineString" || geometry?.type === "MultiLineString";

  if (isPolygon && geojson) {
    // Геометрия приходит из queryRenderedFeatures, то есть обрезанной по границе
    // тайла и только из одного тайла. Для зоны крупнее тайла — а на рабочих
    // зумах это почти любая зона — посчитанная площадь занижена в разы.
    // Поэтому значение из самого документа имеет приоритет, а вычисленное
    // подаётся как оценка, а не как факт.
    const fromDocument = documentArea(feature.properties);
    if (fromDocument) {
      rows.push(["Площадь", formatArea(fromDocument.value)]);
      footnote = `по атрибуту «${fromDocument.label}» из документа`;
    } else {
      rows.push(["Площадь", `≈ ${formatArea(area(geojson))}`]);
      footnote = "оценка по видимому фрагменту: геометрия обрезана границей тайла";
    }
  }

  if (isLine && geojson) {
    rows.push(["Длина", `≈ ${formatLength(length(geojson, { units: "kilometers" }) * 1000)}`]);
    footnote = "оценка по видимому фрагменту: геометрия обрезана границей тайла";
  }

  // Название слоя повторять незачем, когда оно и стоит в заголовке карточки:
  // у справки о точке заголовком служит как раз название функциональной зоны.
  if (feature.layerTitle !== heading) {
    rows.push(["Слой", feature.layerTitle]);
  }

  if (rows.length === 0 && !footnote) return null;

  return el(
    "div",
    { class: "card__metrics-block" },
    // Пустой список метрик — это не «ничего», а серая плашка на всю ширину
    // карточки. У справки о точке метрик не бывает вовсе: ни площади, ни
    // длины взять неоткуда, зато сноска об источнике нужна всегда.
    rows.length > 0
      ? el(
          "dl",
          { class: "card__metrics" },
          ...rows.flatMap(([term, value]) => [
            el("dt", { text: term }),
            el("dd", { text: value }),
          ]),
        )
      : null,
    footnote ? el("p", { class: "card__footnote", text: footnote }) : null,
  );
}

export function formatArea(squareMeters: number): string {
  if (squareMeters >= 1_000_000) {
    return `${formatNumber(squareMeters / 1_000_000, 2)} км² (${formatNumber(squareMeters / 10_000, 1)} га)`;
  }
  if (squareMeters >= 10_000) {
    return `${formatNumber(squareMeters / 10_000, 2)} га (${formatNumber(squareMeters, 0)} м²)`;
  }
  return `${formatNumber(squareMeters, 0)} м²`;
}

export function formatLength(meters: number): string {
  if (meters >= 1000) {
    return `${formatNumber(meters / 1000, 2)} км (${formatNumber(meters, 0)} м)`;
  }
  return `${formatNumber(meters, 1)} м`;
}

function formatNumber(value: number, digits: number): string {
  return value.toLocaleString("ru-RU", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

/** Служебные поля тайлов, которые пользователю ничего не говорят. */
const HIDDEN_FIELDS = new Set(["_id", "tippecanoe_id", "layer", "@id"]);

function attributesTable(properties: Record<string, unknown>): HTMLElement {
  const entries = Object.entries(properties)
    .filter(([key, value]) => !HIDDEN_FIELDS.has(key) && value !== null && value !== "")
    .sort(([a], [b]) => a.localeCompare(b, "ru"));

  if (entries.length === 0) {
    return el("p", { class: "card__empty", text: "Атрибутов у объекта нет." });
  }

  return el(
    "table",
    { class: "card__attributes" },
    el("caption", { class: "card__attributes-caption", text: "Атрибуты объекта" }),
    el(
      "tbody",
      {},
      ...entries.map(([key, value]) =>
        el(
          "tr",
          {},
          el("th", { attrs: { scope: "row" }, text: key }),
          el("td", { text: formatValue(value) }),
        ),
      ),
    ),
  );
}

function formatValue(value: unknown): string {
  if (typeof value === "number") return value.toLocaleString("ru-RU");
  if (typeof value === "boolean") return value ? "да" : "нет";
  return String(value);
}
